import React from 'react';
import { Modal, Button, Loader, Message, List, Label } from 'semantic-ui-react';
import type { FastChipMovementEventDto, ICcIdResponse } from '../../types/dtos';
import { useFastChipMovementHistory } from '../../hooks/icCids/useFastChipMovementHistory';
import { formatCnpj } from '../../services/utils/stringUtils';

function movementOccurredAtIso(ev: FastChipMovementEventDto): string {
  return ev.occurredAt ?? ev.occurredAtUtc ?? '';
}

function tryParseMovementMetadata(metadataJson?: string | null): Record<string, unknown> | null {
  if (!metadataJson?.trim()) return null;
  try {
    const parsed = JSON.parse(metadataJson) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // ignorar
  }
  return null;
}

function truthyMetaFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

/** Título principal do evento (metadata enriquecida quando existir; fallback para rótulos legados). */
function movementEventHeadline(ev: FastChipMovementEventDto): string {
  const meta = tryParseMovementMetadata(ev.metadataJson);
  const raw = String(ev.eventType ?? '');
  const pascal =
    raw.length > 0 && raw[0] === raw[0].toLowerCase()
      ? raw[0].toUpperCase() + raw.slice(1)
      : raw;

  const markedFastUsado = truthyMetaFlag(meta?.markedFastUsado);

  if (pascal === 'ReleasedFromContract' && markedFastUsado) {
    return 'Removido do contrato na troca FAST; chip anterior marcado como usado (fora do stock)';
  }

  const fastUsadoNaTroca = truthyMetaFlag(meta?.fastUsado);

  if (pascal === 'ContractLineFastchipIccidSwapped') {
    if (fastUsadoNaTroca) {
      return 'Troca de CCID FAST na linha do contrato (chip anterior tratado como usado)';
    }
    return 'Troca de CCID FAST na linha do contrato';
  }

  const map: Record<string, string> = {
    AssignedToContract: 'Vinculado a contrato (consumo)',
    ReleasedFromContract: 'Liberado / retorno ao pool',
    Imported: 'Importado',
    Created: 'Criado no sistema',
    Deleted: 'Removido (administrativo)',
    MadeAvailable: 'Disponibilizado',
    MadeUnavailable: 'Indisponibilizado',
  };

  if (pascal === 'MadeUnavailable') {
    const r = (ev.reason ?? '').toLowerCase();
    if (r.includes('fast usado')) {
      return 'Indisponibilizado no stock (FAST usado)';
    }
  }

  return map[pascal] || raw;
}

function safeFormatMetadata(metadataJson?: string | null): React.ReactNode {
  if (!metadataJson?.trim()) return null;
  try {
    const parsed = JSON.parse(metadataJson) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed as Record<string, unknown>);
      if (entries.length === 0) return null;
      return (
        <List size="small" relaxed style={{ marginTop: 8 }}>
          {entries.map(([k, v]) => (
            <List.Item key={k}>
              <strong>{k}:</strong> {String(v)}
            </List.Item>
          ))}
        </List>
      );
    }
  } catch {
    // texto livre
  }
  return (
    <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 140, overflow: 'auto', marginTop: 8 }}>
      {metadataJson}
    </pre>
  );
}

export interface FastChipMovementHistoryModalProps {
  iccid: ICcIdResponse | null;
  open: boolean;
  onClose: () => void;
}

export const FastChipMovementHistoryModal: React.FC<FastChipMovementHistoryModalProps> = ({
  iccid,
  open,
  onClose,
}) => {
  const controlId = iccid?.id ?? null;
  const {
    events: movementHistoryEvents,
    loading: movementHistoryLoading,
    error: movementHistoryError,
    reload: reloadMovementHistory,
  } = useFastChipMovementHistory(controlId, open && !!controlId);

  return (
    <Modal open={open && !!iccid} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        Histórico de movimentos FAST
        {iccid && (
          <div
            style={{
              marginTop: 8,
              fontWeight: 400,
              fontSize: '0.95rem',
              color: '#666',
            }}
          >
            CCID <code>{iccid.ccId}</code>
            <span style={{ marginLeft: 8, fontSize: '0.85rem' }}>(ID {iccid.id})</span>
          </div>
        )}
      </Modal.Header>
      <Modal.Content scrolling style={{ maxHeight: '70vh' }}>
        {movementHistoryLoading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Loader active inline="centered">
              A carregar histórico…
            </Loader>
          </div>
        )}
        {!movementHistoryLoading && movementHistoryError && (
          <Message negative>
            <Message.Header>Erro</Message.Header>
            <p>{movementHistoryError}</p>
            <Button size="small" style={{ marginTop: 10 }} onClick={() => void reloadMovementHistory()}>
              Tentar novamente
            </Button>
          </Message>
        )}
        {!movementHistoryLoading && !movementHistoryError && movementHistoryEvents.length === 0 && (
          <Message info>
            <Message.Header>Sem eventos</Message.Header>
            <p>Não há movimentos registados para este ICCID, ou o endpoint ainda não está disponível na API.</p>
          </Message>
        )}
        {!movementHistoryLoading && !movementHistoryError && movementHistoryEvents.length > 0 && (
          <List divided relaxed size="large">
            {movementHistoryEvents.map((ev, idx) => (
              <List.Item key={ev.id ?? `${movementOccurredAtIso(ev)}-${idx}`}>
                <List.Content>
                  <List.Header as="h4" style={{ marginBottom: 6 }}>
                    {movementEventHeadline(ev)}
                    <Label basic size="small" style={{ marginLeft: 8, fontWeight: 400 }}>
                      {new Date(movementOccurredAtIso(ev) || 0).toLocaleString('pt-BR')}
                    </Label>
                  </List.Header>
                  <List.Description>
                    <List relaxed size="small">
                      {ev.changedBy ? (
                        <List.Item>
                          <strong>Utilizador / origem:</strong> {ev.changedBy}
                        </List.Item>
                      ) : null}
                      {ev.reason ? (
                        <List.Item>
                          <strong>Motivo:</strong> {ev.reason}
                        </List.Item>
                      ) : null}
                      {(ev.ccIdSnapshot || ev.iccIdSnapshot) && (
                        <List.Item>
                          <strong>Snapshots CCID/ICCID:</strong>{' '}
                          {ev.ccIdSnapshot ? <code>{ev.ccIdSnapshot}</code> : null}
                          {ev.ccIdSnapshot && ev.iccIdSnapshot ? ' · ' : null}
                          {ev.iccIdSnapshot ? <code>{ev.iccIdSnapshot}</code> : null}
                        </List.Item>
                      )}
                      {(ev.segmentSnapshot || ev.clusterSnapshot || ev.stateSnapshot) && (
                        <List.Item>
                          <strong>Segmento / cluster / UF:</strong>{' '}
                          {[ev.segmentSnapshot, ev.clusterSnapshot, ev.stateSnapshot].filter(Boolean).join(' · ') ||
                            '—'}
                        </List.Item>
                      )}
                      {(ev.clientNameSnapshot || ev.clientCnpjSnapshot) && (
                        <List.Item>
                          <strong>Cliente (snapshot):</strong> {ev.clientNameSnapshot || '—'}
                          {ev.clientCnpjSnapshot ? ` · ${formatCnpj(ev.clientCnpjSnapshot)}` : ''}
                        </List.Item>
                      )}
                      {(ev.contractId || ev.contractNumberSnapshot || ev.contractIdSnapshot) && (
                        <List.Item>
                          <strong>Contrato:</strong>{' '}
                          {ev.contractId || ev.contractNumberSnapshot || ev.contractIdSnapshot}
                        </List.Item>
                      )}
                    </List>
                    {safeFormatMetadata(ev.metadataJson)}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
          </List>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          primary
          onClick={() => void reloadMovementHistory()}
          disabled={movementHistoryLoading}
          loading={movementHistoryLoading}
        >
          Atualizar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
